import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { classifyLine } from './lineGroups';
import { isTransferDesignated } from './lineFilters';
import { effectiveQuantity } from './lineQuantity';
import { roundQuantity } from './needQuantitySpread';

export interface NeedSplit {
  total: number;
  /** What the sales-order lines take. */
  backOrder: number;
  /** What the stock lines take; null when the need has none. */
  stock: number | null;
  /**
   * The vendor's order multiple the open lines agree on, and how many packs
   * the total makes (null when it is not a whole number of them).
   */
  pack: { size: number; count: number | null } | null;
}

/**
 * How a need's open lines split one order (GEN-2938): back orders and stock
 * received together, and whether the total lands on the vendor's pack size.
 * The pack is checked, never snapped: per-line quantities need not be packs.
 */
export function needSplit(open: readonly AssignmentLine[]): NeedSplit {
  let backOrder = 0;
  let stock: number | null = null;
  for (const line of open) {
    const quantity = effectiveQuantity(line);
    if (classifyLine(line) === 'stock') stock = (stock ?? 0) + quantity;
    else backOrder += quantity;
  }
  const total = roundQuantity(backOrder + (stock ?? 0));
  const size = agreedPackSize(open);
  const packs = size == null ? null : total / size;
  return {
    total,
    backOrder: roundQuantity(backOrder),
    stock: stock == null ? null : roundQuantity(stock),
    pack:
      size == null || packs == null
        ? null
        : { size, count: Number.isInteger(packs) ? packs : null },
  };
}

/**
 * The order multiple every open line that has one agrees on, above 1. None
 * when the lines ship to different places, so go on separate orders, or when
 * any moves stock by transfer: the need's total is then no one purchase
 * order's quantity.
 */
function agreedPackSize(open: readonly AssignmentLine[]): number | null {
  if (open.some(isTransferDesignated)) return null;
  const shipTos = new Set(open.map((line) => line.shipToWarehouseId));
  if (shipTos.size > 1) return null;
  const sizes = new Set(
    open.flatMap((line) => {
      const size = line.topOption?.vendorIncrementQuantity;
      return size != null && size > 1 ? [size] : [];
    }),
  );
  return sizes.size === 1 ? [...sizes][0] : null;
}
